import { Route, Routes, Navigate } from "react-router";
import { AppThemeSync } from "@/providers/app-theme-sync";
import { SmartRedirect } from "@/components/application/smart-redirect";
import { CompanyType } from "@/types/api/admin";
import { NotFound } from "@/pages/not-found";
import { LandingPage } from "@/pages/landing";
import { ContactPage } from "@/pages/contact";
import { LoginPage } from "@/pages/auth/login";
import { ResetPasswordPage } from "@/pages/auth/reset-password";
import { NewPasswordPage } from "@/pages/auth/new-password";
import { AdminLayout } from "@/layouts/admin-layout";
import { ShipperLayout } from "@/layouts/shipper-layout";
import { FreightForwarderLayout } from "@/layouts/freight-forwarder-layout";
import { AuthRouteGuard } from "@/components/application/route-guard/auth-route-guard";
import { DashboardPage } from "@/pages/admin/dashboard";
import { CompaniesListPage } from "@/pages/admin/companies/index";
import { CompanyFormPage } from "@/pages/admin/companies/form";
import { CompanyTeamPage } from "@/pages/admin/companies/team";
import { CompanyMenuConfigPage } from "@/pages/admin/companies/[id]/menu-config";
import { VerifyInvitationPage } from "@/pages/auth/verify-invitation";
import { SettingsPage } from "@/pages/admin/settings";
import { MenuConfigTab } from "@/pages/admin/settings/menu-config-tab";
import { PlatformSettingsTab } from "@/pages/admin/settings/platform-settings-tab";
import { OutboundEmailTemplatesTab } from "@/pages/admin/settings/outbound-email-templates-tab";
import { OutboundEmailTemplateFormPage } from "@/pages/admin/settings/outbound-email-templates/outbound-email-template-form-page";
import { TrackingManagementTab } from "@/pages/admin/settings/tracking-management/tracking-management-tab";
import { PipelineDebugTab } from "@/pages/admin/settings/pipeline-debug-tab";
import { DocumentsAdminPage } from "@/pages/admin/documents/index";
import { DocumentTypesPage } from "@/pages/admin/documents/types/index";
import { DocumentSchemasPage } from "@/pages/admin/documents/schemas/index";
import { DocumentAttributesPage } from "@/pages/admin/documents/attributes/index";
import { DocumentTypeSchemasPage } from "@/pages/admin/documents/types/[documentType]/schemas/index";
import { ParserRulesPage } from "@/pages/admin/documents/types/[documentType]/schemas/[schemaId]/parser-rules/index";
import { SchemaAttributesPage } from "@/pages/admin/documents/schemas/[schemaId]/attributes";
import { DocConnectionManagerPage } from "@/pages/admin/documents/connections/index";
import { RequirementsMatrixPage } from "@/pages/admin/documents/requirements/index";
import { TrainingDocumentsPage } from "@/pages/admin/documents/training/index";
import { TrainingDocumentDetailPage } from "@/pages/admin/documents/training/[trainingDocId]/index";
import { WorkflowsListPage } from "@/pages/admin/workflows/index";
import { WorkflowDetailPage } from "@/pages/admin/workflows/[workflowId]/index";
import { WorkflowEditPage } from "@/pages/admin/workflows/[workflowId]/edit";
import { KnowledgeBaseListPage } from "@/pages/admin/knowledge/index";
import { KnowledgeBaseNewPage } from "@/pages/admin/knowledge/new";
import { KnowledgeBaseDetailPage } from "@/pages/admin/knowledge/[knowledgeBaseId]/index";
import { TagsAdminPage } from "@/pages/admin/tags/index";
import { AdminContainersPage } from "@/pages/admin/containers/index";
import { AnalyticsPage } from "@/pages/admin/analytics/index";
import { ShipperAskEvePage } from "@/pages/shipper/ask-eve";
import { ShipperControlHubPage } from "@/pages/shipper/control-hub";
import { ShipperContainersPage } from "@/pages/shipper/containers/index";
import { ContainerFormPage } from "@/pages/shipper/containers/form";
import { ContainerDetailPage } from "@/pages/shipper/containers/[id]";
import { ContainerUploadPage } from "@/pages/shipper/containers/upload";
import ShipperDocumentReviewPage from "@/pages/shipper/containers/document-review";
import ShipperDocumentHistoryPage from "@/pages/shipper/containers/document-history";
import UnifiedDocumentViewerPage from "@/pages/shipper/containers/documents/[documentId]";
import { ShipperContainersWorldMapPage } from "@/pages/shipper/containers/world-map";
import { ShipperBusinessPartnersPage } from "@/pages/shipper/business-partners/index";
import { BusinessPartnerDetailPage } from "@/pages/shipper/business-partners/[id]";
import { BusinessPartnerFormPage } from "@/pages/shipper/business-partners/form";
import { ShipperAddressBookPage } from "@/pages/shipper/address-book/index";
import { ShipperActivityPage } from "@/pages/shipper/activity/index";
import { ShipperIssuesPage } from "@/pages/shipper/issues/index";
import { ShipperReviewCenterPage } from "@/pages/shipper/review-center/index";
import { ShipperQuotesPage } from "@/pages/shipper/quotes/index";
import { ShipperQuoteDetailPage } from "@/pages/shipper/quotes/[id]";
import { ShipperQuoteResponseDetailPage } from "@/pages/shipper/quotes/[id]/response/[responseId]";
import { ShipperQuotesSettingsPage } from "@/pages/shipper/quotes/settings";
import { ShipperOrdersPage } from "@/pages/shipper/orders/index";
import { ShipperOrderDetailPage } from "@/pages/shipper/orders/[id]";
import { ShipperDocumentsPage } from "@/pages/shipper/documents/index";
import { ShipperDocumentDetailPage } from "@/pages/shipper/documents/[documentId]";
import { ReceiverLayout } from "@/layouts/receiver-layout";
import { ReceiverAskEvePage } from "@/pages/receiver/ask-eve";
import { ReceiverControlHubPage } from "@/pages/receiver/control-hub";
import { FreightForwarderAskEvePage } from "@/pages/freight-forwarder/ask-eve";
import { FreightForwarderControlHubPage } from "@/pages/freight-forwarder/control-hub";
import { FreightForwarderQuotesPage } from "@/pages/freight-forwarder/quotes/index";
import { FreightForwarderQuoteDetailPage } from "@/pages/freight-forwarder/quotes/[id]";
import { FreightForwarderQuotesSettingsPage } from "@/pages/freight-forwarder/quotes/settings";
import { FreightForwarderActivityPage } from "@/pages/freight-forwarder/activity";
import { FreightForwarderAddressBookPage } from "@/pages/freight-forwarder/address-book";
import { FreightForwarderContainersPage } from "@/pages/freight-forwarder/containers/index";
import { ReceiverCalendarPage } from "@/pages/receiver/calendar";
import { ReceiverShippersPage } from "@/pages/receiver/shippers/index";
import { ShipperDetailPage } from "@/pages/receiver/shippers/[id]";
import { ReceiverContainersPage } from "@/pages/receiver/containers/index";
import { ReceiverContainerDetailPage } from "@/pages/receiver/containers/[id]";
import { ReceiverAddressBookPage } from "@/pages/receiver/address-book/index";
import ReceiverDocumentReviewPage from "@/pages/receiver/containers/document-review";
import ReceiverDocumentHistoryPage from "@/pages/receiver/containers/document-history";
import ReceiverContainersWorldMapPage from "@/pages/receiver/containers/world-map";
import { ReceiverActivityPage } from "@/pages/receiver/activity/index";
import { ReceiverOrdersPage } from "@/pages/receiver/orders/index";
import { ReceiverOrderDetailPage } from "@/pages/receiver/orders/[id]";
import { ReceiverNewOrderPage } from "@/pages/receiver/orders/new";
import { ProfilePage } from "@/pages/shared/profile";
import { ProfileCompanyEditPage } from "@/pages/shared/profile-company-edit";
import { PublicDocumentReviewPage } from "@/pages/public/document-review";
import { TermsPage } from "@/pages/legal/terms";
import { PrivacyPage } from "@/pages/legal/privacy";

export function AppRoutes() {
    return (
        <>
            <AppThemeSync />
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/review/:token" element={<PublicDocumentReviewPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />

            {/* Auth Routes */}
            <Route path="/auth/login" element={<LoginPage />} />
            <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/new-password" element={<NewPasswordPage />} />
            <Route path="/auth/verify-invitation" element={<VerifyInvitationPage />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin" element={<AuthRouteGuard><AdminLayout /></AuthRouteGuard>}>
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="companies" element={<CompaniesListPage />} />
                <Route path="companies/new" element={<CompanyFormPage />} />
                <Route path="companies/:id/edit" element={<CompanyFormPage />} />
                <Route path="companies/:id/team" element={<CompanyTeamPage />} />
                <Route path="companies/:id/menu-config" element={<CompanyMenuConfigPage />} />
                <Route path="documents" element={<DocumentsAdminPage />}>
                    <Route index element={<Navigate to="/admin/documents/types" replace />} />
                    <Route path="types" element={<DocumentTypesPage />} />
                    <Route path="types/:documentTypeId/schemas" element={<DocumentTypeSchemasPage />} />
                    <Route path="types/:documentTypeId/schemas/:schemaId/attributes" element={<SchemaAttributesPage />} />
                    <Route path="types/:documentTypeId/schemas/:schemaId/parser-rules" element={<ParserRulesPage />} />
                    <Route path="attributes" element={<DocumentAttributesPage />} />
                    <Route path="schemas" element={<DocumentSchemasPage />} />
                    <Route path="schemas/:schemaId/attributes" element={<SchemaAttributesPage />} />
                    <Route path="connections" element={<DocConnectionManagerPage />} />
                    <Route path="requirements" element={<RequirementsMatrixPage />} />
                    <Route path="training" element={<TrainingDocumentsPage />} />
                    <Route path="training/:trainingDocId" element={<TrainingDocumentDetailPage />} />
                </Route>
                <Route path="workflows" element={<WorkflowsListPage />} />
                <Route path="workflows/new" element={<WorkflowEditPage />} />
                <Route path="workflows/:workflowId/edit" element={<WorkflowEditPage />} />
                <Route path="workflows/:workflowId" element={<WorkflowDetailPage />} />
                <Route path="knowledge" element={<KnowledgeBaseListPage />} />
                <Route path="knowledge/new" element={<KnowledgeBaseNewPage />} />
                <Route path="knowledge/:knowledgeBaseId" element={<KnowledgeBaseDetailPage />} />
                <Route path="tags" element={<TagsAdminPage />} />
                <Route path="containers" element={<AdminContainersPage />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="settings" element={<SettingsPage />}>
                    <Route index element={<Navigate to="menu-config" replace />} />
                    <Route path="menu-config" element={<MenuConfigTab />} />
                    <Route path="platform-settings" element={<PlatformSettingsTab />} />
                    <Route path="outbound-email-templates" element={<OutboundEmailTemplatesTab />} />
                    <Route path="outbound-email-templates/new" element={<OutboundEmailTemplateFormPage />} />
                    <Route path="outbound-email-templates/:templateId/edit" element={<OutboundEmailTemplateFormPage />} />
                    <Route path="tracking-management" element={<TrackingManagementTab />} />
                    <Route path="pipeline-debug" element={<PipelineDebugTab />} />
                    <Route path="processing-journal" element={<Navigate to="../pipeline-debug" replace />} />
                </Route>
                <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* SHIPPER Routes */}
            <Route path="/shipper" element={<SmartRedirect companyType={CompanyType.SENDER} defaultPath="/shipper/control-hub" />} />
            <Route path="/shipper" element={<AuthRouteGuard><ShipperLayout /></AuthRouteGuard>}>
                <Route path="ask-eve" element={<ShipperAskEvePage />} />
                <Route path="control-hub" element={<ShipperControlHubPage />} />
                <Route path="dashboard" element={<SmartRedirect companyType={CompanyType.SENDER} defaultPath="/shipper/control-hub" />} />
                <Route path="containers" element={<ShipperContainersPage />} />
                <Route path="live-tracking" element={<ShipperContainersWorldMapPage />} />
                <Route path="containers/world-map" element={<Navigate to="/shipper/live-tracking" replace />} />
                <Route path="containers/new" element={<ContainerFormPage />} />
                <Route path="containers/:id" element={<ContainerDetailPage />} />
                <Route path="containers/:id/upload" element={<ContainerUploadPage />} />
                <Route path="containers/:containerId/documents/:documentId/review" element={<ShipperDocumentReviewPage />} />
                {/* New unified document viewer */}
                <Route path="containers/:containerId/documents/:documentId" element={<UnifiedDocumentViewerPage />} />
                <Route path="containers/:containerId/documents/:documentId/history" element={<ShipperDocumentHistoryPage />} />
                <Route path="containers/:id/edit" element={<ContainerFormPage />} />
                <Route path="documents" element={<ShipperDocumentsPage />} />
                <Route path="documents/:documentId" element={<ShipperDocumentDetailPage />} />
                <Route path="business-partners" element={<ShipperBusinessPartnersPage />} />
                <Route path="business-partners/new" element={<BusinessPartnerFormPage />} />
                <Route path="business-partners/:id/edit" element={<BusinessPartnerFormPage />} />
                <Route path="business-partners/:id" element={<BusinessPartnerDetailPage />} />
                {/* Redirects from old receivers routes */}
                <Route path="receivers" element={<Navigate to="/shipper/business-partners" replace />} />
                <Route path="receivers/:id" element={<Navigate to="/shipper/business-partners" replace />} />
                <Route path="activity" element={<ShipperActivityPage />} />
                <Route path="issues" element={<ShipperIssuesPage />} />
                <Route path="review-center" element={<ShipperReviewCenterPage />} />
                <Route path="quotes" element={<ShipperQuotesPage />} />
                <Route path="quotes/:id" element={<ShipperQuoteDetailPage />} />
                <Route path="quotes/:id/response/:responseId" element={<ShipperQuoteResponseDetailPage />} />
                <Route path="quotes/settings" element={<ShipperQuotesSettingsPage />} />
                <Route path="orders" element={<ShipperOrdersPage />} />
                <Route path="orders/:id" element={<ShipperOrderDetailPage />} />
                <Route path="team" element={<Navigate to="/shipper/profile?tab=team" replace />} />
                <Route path="address-book" element={<ShipperAddressBookPage />} />
                <Route path="settings" element={<Navigate to="/shipper/profile" replace />} />
                <Route path="profile/companies/:companyId/edit" element={<ProfileCompanyEditPage />} />
                <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* RECEIVER Routes */}
            <Route path="/receiver" element={<SmartRedirect companyType={CompanyType.RECEIVER} defaultPath="/receiver/control-hub" />} />
            <Route path="/receiver" element={<AuthRouteGuard><ReceiverLayout /></AuthRouteGuard>}>
                <Route path="ask-eve" element={<ReceiverAskEvePage />} />
                <Route path="control-hub" element={<ReceiverControlHubPage />} />
                <Route path="dashboard" element={<SmartRedirect companyType={CompanyType.RECEIVER} defaultPath="/receiver/control-hub" />} />
                <Route path="calendar" element={<ReceiverCalendarPage />} />
                <Route path="shippers" element={<ReceiverShippersPage />} />
                <Route path="shippers/:id" element={<ShipperDetailPage />} />
                <Route path="containers" element={<ReceiverContainersPage />} />
                <Route path="live-tracking" element={<ReceiverContainersWorldMapPage />} />
                <Route path="containers/world-map" element={<Navigate to="/receiver/live-tracking" replace />} />
                <Route path="containers/:id" element={<ReceiverContainerDetailPage />} />
                <Route path="containers/:containerId/documents/:documentId/review" element={<ReceiverDocumentReviewPage />} />
                <Route path="containers/:containerId/documents/:documentId/history" element={<ReceiverDocumentHistoryPage />} />
                <Route path="activity" element={<ReceiverActivityPage />} />
                <Route path="orders" element={<ReceiverOrdersPage />} />
                <Route path="orders/new" element={<ReceiverNewOrderPage />} />
                <Route path="orders/:id" element={<ReceiverOrderDetailPage />} />
                <Route path="team" element={<Navigate to="/receiver/profile?tab=team" replace />} />
                <Route path="address-book" element={<ReceiverAddressBookPage />} />
                <Route path="settings" element={<Navigate to="/receiver/profile" replace />} />
                <Route path="profile/companies/:companyId/edit" element={<ProfileCompanyEditPage />} />
                <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* FREIGHT_FORWARDER Routes */}
            <Route path="/freight-forwarder" element={<SmartRedirect companyType={CompanyType.FREIGHT_FORWARDER} defaultPath="/freight-forwarder/control-hub" />} />
            <Route path="/freight-forwarder" element={<AuthRouteGuard><FreightForwarderLayout /></AuthRouteGuard>}>
                <Route path="ask-eve" element={<FreightForwarderAskEvePage />} />
                <Route path="control-hub" element={<FreightForwarderControlHubPage />} />
                <Route path="dashboard" element={<SmartRedirect companyType={CompanyType.FREIGHT_FORWARDER} defaultPath="/freight-forwarder/control-hub" />} />
                <Route path="quotes" element={<FreightForwarderQuotesPage />} />
                <Route path="quotes/:id" element={<FreightForwarderQuoteDetailPage />} />
                <Route path="quotes/settings" element={<FreightForwarderQuotesSettingsPage />} />
                <Route path="containers" element={<FreightForwarderContainersPage />} />
                <Route path="activity" element={<FreightForwarderActivityPage />} />
                <Route path="team" element={<Navigate to="/freight-forwarder/profile?tab=team" replace />} />
                <Route path="address-book" element={<FreightForwarderAddressBookPage />} />
                <Route path="profile/companies/:companyId/edit" element={<ProfileCompanyEditPage />} />
                <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* 404 Route */}
            <Route path="*" element={<NotFound />} />
        </Routes>
        </>
    );
}

